import React, { useState } from "react";

import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { ColumnBodyOptions, ColumnEditorOptions } from "primereact/column";
import { DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { BackgroundColorSetting, ConditionColor } from "types/types";
import ColorPicker from "../color-picker/ColorPicker";
import DataTable from "../data-table/DataTable";
import { DatasetDropdown } from "../dropdowns/dataset/DatasetDropdown";

type BackgroundConfigByDatasetProps = {
  bgColorSetting: BackgroundColorSetting | undefined;
  setBgColorSetting: (bgColorSetting: BackgroundColorSetting) => void;
};

const BackgroundConfigByDataset: React.FC<BackgroundConfigByDatasetProps> = ({
  bgColorSetting,
  setBgColorSetting,
}) => {
  const [dialog, setDialog] = useState(false);

  const handleDatasetChange = (dataset: string | null) => {
    if (!dataset) return;
    setBgColorSetting({ ...(bgColorSetting ?? { conditionalColors: [] }), dataset });
  };

  return (
    <>
      <fieldset className="bg-gray-100 p-2 border-round border-1 border-gray-300 flex justify-content-center align-items-center w-full">
        <legend className="bg-gray-100 border-round">{T("word.dynamic-background-color")}</legend>
        <DatasetDropdown value={bgColorSetting?.dataset} onChange={handleDatasetChange} />
        <Button
          icon="pi pi-list text-xl"
          className="ml-2 mr-1 component-editor-button icon-black"
          text
          onClick={() => setDialog(true)}
        />
      </fieldset>
      <Dialog
        header={T("action.set-custom-background-color")}
        visible={dialog}
        onHide={() => setDialog(false)}
        style={{ minWidth: "50rem" }}
        modal
        resizable
        draggable
      >
        <BackgroundPropertiesEditor bgColorSetting={bgColorSetting} setBgColorSetting={setBgColorSetting} />
      </Dialog>
    </>
  );
};

export default BackgroundConfigByDataset;

const BackgroundPropertiesEditor: React.FC<BackgroundConfigByDatasetProps> = ({
  bgColorSetting,
  setBgColorSetting,
}) => {
  const renderNumInput = (options: ColumnEditorOptions) => {
    return (
      <InputNumber
        value={options.value}
        onChange={e => options.editorCallback?.(e.value)}
        maxFractionDigits={2}
        useGrouping={false}
      />
    );
  };

  const renderColorPicker = (options: ColumnEditorOptions) => {
    return (
      <ColorPicker value={options.value} onValueChange={e => options.editorCallback?.(e.target.value)} />
    );
  };

  const renderColorPickerBody = (rowData: ConditionColor) => {
    return <input type="color" className="border-round" value={rowData.color} disabled />;
  };

  const renderRemove = (_rowData: ConditionColor, options: ColumnBodyOptions) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-text p-button-danger"
        onClick={() => {
          if (!bgColorSetting) return;
          const newConditionalColors = [...bgColorSetting.conditionalColors];
          newConditionalColors.splice(options.rowIndex, 1);
          setBgColorSetting({ ...bgColorSetting, conditionalColors: newConditionalColors });
        }}
      />
    );
  };

  const handleAddRow = () => {
    const newConditionalColors = [
      ...(bgColorSetting?.conditionalColors ?? []),
      { value: 0, color: "#ffffff", opacity: 0.1 },
    ];
    setBgColorSetting({ ...bgColorSetting!, conditionalColors: newConditionalColors });
  };

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    setBgColorSetting({
      ...bgColorSetting!,
      conditionalColors: bgColorSetting!.conditionalColors.map((cc, i) =>
        i === e.index ? (e.newData as ConditionColor) : cc
      ),
    });
  };

  const columns: TableColumn<ConditionColor>[] = [
    {
      field: "value",
      header: T("word.value"),
      columnKey: "value",
      className: "ml-2",
      editor: renderNumInput,
    },
    {
      field: "opacity",
      header: T("word.opacity"),
      columnKey: "opacity",
      editor: renderNumInput,
    },
    {
      field: "color",
      header: T("word.background-color"),
      columnKey: "color",
      editor: renderColorPicker,
      body: renderColorPickerBody,
    },
    {
      rowEditor: true,
      columnKey: "edit",
      align: "right",
      header: "",
    },
    {
      columnKey: "remove",
      header: "",
      style: { width: "0" },
      body: renderRemove,
    },
  ];

  const header = (
    <Button
      type="button"
      label={T("action.add-row")}
      icon="pi pi-plus"
      className="component-editor-button icon-green w-2"
      onClick={handleAddRow}
    />
  );

  return (
    <DataTable
      data={bgColorSetting?.conditionalColors ?? []}
      columns={columns}
      tableConfig={{ paginator: false, header, editMode: "row", onRowEditComplete }}
    />
  );
};
